/** D51: the lead mechanic plus anyone else also working the job. Manager/Admin add or remove. */
import { useState } from 'react';
import { api, errorText } from '../lib/api';
import { useMechanics } from '../lib/hooks';
import type { WorkOrderDetail } from '../lib/types';
import { Button, Card, ErrorText, Select } from '../components/ui';

const OPEN_FOR_HELPERS = ['ASSIGNED', 'IN_PROGRESS', 'ON_HOLD', 'READY_FOR_REVIEW'];

export function HelpersCard({ w, canEdit, onChange }: { w: WorkOrderDetail; canEdit: boolean; onChange: () => void }) {
  const active = w.helpers.filter((h) => !h.removedAt);
  const editable = canEdit && Boolean(w.assignedUserId) && OPEN_FOR_HELPERS.includes(w.status);
  const mechanics = useMechanics(editable);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      setPick('');
      onChange();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const choices = mechanics.data?.filter((m) => m.id !== w.assignedUserId && !active.some((h) => h.user.id === m.id)) ?? [];

  return (
    <Card title="Mechanics on this job">
      <ErrorText error={error} />
      <ul className="space-y-2 text-sm">
        <li>
          <span className="font-semibold">{w.assignedUser?.name ?? 'Unassigned'}</span>
          {w.assignedUser && <span className="text-slate-500"> · lead</span>}
        </li>
        {active.map((h) => (
          <li key={h.id} className="flex items-center justify-between gap-2">
            <span>{h.user.name} <span className="text-slate-500">· also working</span></span>
            {editable && (
              <Button variant="ghost" onClick={() => confirm(`Take ${h.user.name} off this job? Their clock is stopped if they're clocked in.`) && run(() => api.post(`/api/work-orders/${w.id}/helpers/${h.user.id}/remove`, {}))}>
                Remove
              </Button>
            )}
          </li>
        ))}
      </ul>
      {editable && choices.length > 0 && (
        <div className="mt-3 flex gap-2">
          <Select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1">
            <option value="">Add another mechanic…</option>
            {choices.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
          <Button disabled={!pick} onClick={() => run(() => api.post(`/api/work-orders/${w.id}/helpers`, { mechanicId: Number(pick) }))}>Add</Button>
        </div>
      )}
      {editable && <p className="mt-2 text-xs text-slate-500">Everyone listed sees the job on their iPad and can clock in, add notes, photos and parts. The lead marks it Ready for Review.</p>}
    </Card>
  );
}
